import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Select, Tag, Typography, message, Input, Badge, Space, Spin } from "antd";
import { SaveOutlined, CheckCircleOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { saveCourseSkills, getCourseFilter } from "../../../appRedux/actions/courses";
import axiosHttp from "../../../appRedux/services/axiosCommon";
import AIGenerateAction from "../../Common/AIGenerateAction";
import AIGeneratorConfig from "../../Common/AIGeneratorConfig";

const { Text } = Typography;

const normalizeSkillsFromAI = (payload) => {
  if (!payload) return [];
  
  const extractName = (item) => {
    if (typeof item === "string") return item.trim();
    if (typeof item === "object" && item !== null) {
      return (item.name || item.skill || item.title || "").trim();
    }
    return "";
  };

  if (Array.isArray(payload)) {
    return payload.map(extractName).filter(Boolean);
  }
  
  const raw =
    payload.skills ||
    payload.core_skills ||
    payload.skill_list ||
    payload.skillList ||
    [];

  if (Array.isArray(raw)) {
    return raw.map(extractName).filter(Boolean);
  }

  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const CourseSkillsTab = ({ courseId, onSaveSuccess }) => {
  const dispatch = useDispatch();
  const { courseInfo, courseFilters } = useSelector((state) => state.courses);
  const [loading, setLoading] = useState(false);
  const [loadingDecisions, setLoadingDecisions] = useState(false);
  const [loadingDisambiguate, setLoadingDisambiguate] = useState(false);
  const [disambiguateConfigVisible, setDisambiguateConfigVisible] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [loadingCourse, setLoadingCourse] = useState(false);

  useEffect(() => {
    const fetchCourseInfo = async () => {
      if (!courseId) return;
      setLoadingCourse(true);
      try {
        const response = await axiosHttp.get(`/v1/course/${courseId}`);
        if (response.data?.status && response.data?.results) {
          dispatch({
            type: "COURSE_INFO_SUCCESS",
            payload: response.data.results,
          });
        }
      } catch (err) {
        console.error("Failed to fetch course details:", err);
        message.error("Failed to load course details.");
      } finally {
        setLoadingCourse(false);
      }
    };

    fetchCourseInfo();
  }, [courseId, dispatch]);
  
  // New State for Deduplication Pipeline
  const [matchedSkills, setMatchedSkills] = useState([]);
  const [newSkills, setNewSkills] = useState([]);

  // Use Hex ID if available, otherwise courseId from params
  const effectiveId = courseInfo?._id || courseId;
  const availableSkills = courseFilters?.skills || [];

  // Hydrate from courseInfo when it loads
  useEffect(() => {
    if (courseInfo && Array.isArray(courseInfo.skills)) {
      const ids = courseInfo.skills.map((skill) =>
        typeof skill === "string" ? skill : skill._id
      );
      setSelectedSkills(ids);
    }
  }, [courseInfo]);

  const options = useMemo(
    () =>
      availableSkills.map((skill) => ({
        value: skill._id,
        label: skill.name || skill.code,
      })),
    [availableSkills]
  );

  const selectedSkillLabels = selectedSkills
    .map((id) => availableSkills.find((skill) => skill._id === id))
    .filter(Boolean)
    .map((skill) => skill.name || skill.code);

  const handleAIGenerateSkills = async (generatedPayload) => {
    const generatedNames = normalizeSkillsFromAI(generatedPayload);
    if (!generatedNames.length) {
      message.error("AI did not return any usable skills.");
      return;
    }

    try {
      message.loading({ content: 'Running deduplication...', key: 'dedup' });
      const response = await axiosHttp.post("/v1/skills/deduplicate", {
        skills: generatedNames
      });

      if (response.data?.status && response.data?.results) {
        const { matched, new: newUnmatched } = response.data.results;
        
        // Filter out matched skills that are already assigned to this course
        const filteredMatched = (matched || []).filter(m => {
            return !(m.matchedSkill && m.matchedSkill._id && selectedSkills.includes(m.matchedSkill._id));
        });

        const initialMatched = filteredMatched.map(m => ({ ...m, decision: 'pending' }));
        const initialNew = (newUnmatched || []).map(n => ({ ...n, name: n.generated, parentId: null, decision: 'pending' }));
        
        if (initialMatched.length === 0 && initialNew.length === 0 && (matched?.length > 0)) {
            message.info({ content: 'All generated skills are already assigned to this course.', key: 'dedup' });
            setMatchedSkills([]);
            setNewSkills([]);
            return;
        }

        setMatchedSkills(initialMatched);
        setNewSkills(initialNew);
        message.success({ content: 'Deduplication complete. Please review.', key: 'dedup' });
      }
    } catch (error) {
      console.error("Dedup error:", error);
      message.error({ content: 'Failed to run deduplication pipeline.', key: 'dedup' });
    }
  };

  const handleDisambiguate = async () => {
    if (!effectiveId || matchedSkills.length === 0) return;
    
    setLoadingDisambiguate(true);
    try {
      message.loading({ content: 'Running AI Disambiguation...', key: 'disambiguate' });
      const response = await axiosHttp.post("/v1/skills/disambiguate", {
        matched: matchedSkills,
        courseId: effectiveId
      });

      if (response.data?.status && response.data?.results) {
        setMatchedSkills(response.data.results);
        message.success({ content: 'Disambiguation complete.', key: 'disambiguate' });
      }
    } catch (error) {
      console.error("Disambiguate error:", error);
      message.error({ content: 'Failed to run AI disambiguation.', key: 'disambiguate' });
    } finally {
      setLoadingDisambiguate(false);
    }
  };

  const updateMatchedDecision = (index, decision) => {
    const newMatched = [...matchedSkills];
    const item = newMatched[index];
    
    if (decision === 'save_as_new') {
      // Move to newSkills
      newMatched.splice(index, 1);
      setNewSkills([...newSkills, { generated: item.generated, name: item.generated, parentId: null, decision: 'pending' }]);
    } else if (decision === 'create_child') {
      newMatched.splice(index, 1);
      setNewSkills([
        ...newSkills,
        {
          generated: item.generated,
          name: item.generated,
          parentId: item.matchedSkill?._id || null,
          suggestedParentName: item.matchedSkill?.name || "",
          decision: 'pending'
        }
      ]);
    } else {
      item.decision = decision;
    }
    setMatchedSkills(newMatched);
  };

  const updateNewSkill = (index, field, value) => {
    const updated = [...newSkills];
    updated[index][field] = value;
    setNewSkills(updated);
  };

  const handleApproveAllNewSkills = () => {
    const updated = newSkills.map(ns => ({ ...ns, decision: 'save' }));
    setNewSkills(updated);
  };

  const handleApproveDecisions = async () => {
    if (!effectiveId) return;
    
    const accepted = matchedSkills
      .filter(m => m.decision === 'accept')
      .map(m => ({ skillId: m.matchedSkill._id }));
      
    const newSkillsPayload = newSkills
      .filter(n => n.decision === 'save')
      .map(n => ({ name: n.name, parentId: n.parentId }));
      
    const rejected = [
      ...matchedSkills.filter(m => m.decision === 'discard').map(m => ({ generated: m.generated })),
      ...newSkills.filter(n => n.decision === 'discard').map(n => ({ generated: n.generated }))
    ];

    if (accepted.length === 0 && newSkillsPayload.length === 0 && rejected.length === 0) {
      message.info("No decisions made.");
      return;
    }

    setLoadingDecisions(true);
    try {
      const response = await axiosHttp.post(`/v1/courses/${effectiveId}/skills/save`, {
        accepted,
        newSkills: newSkillsPayload,
        rejected
      });

      if (response.data?.status && response.data?.results) {
        const skillsAdded = response.data.results.skillsAdded || [];
        const addedIds = skillsAdded.map(s => s._id);
        const newSelected = [...new Set([...selectedSkills, ...addedIds])];
        
        setSelectedSkills(newSelected);
        
        // Clear review UI
        setMatchedSkills(matchedSkills.filter(m => m.decision === 'pending'));
        setNewSkills(newSkills.filter(n => n.decision === 'pending'));
        
        message.success(`Successfully saved ${skillsAdded.length} skills!`);
        if (newSkillsPayload.length > 0) {
          dispatch(getCourseFilter({})); // Force update of master skills list
        }
        if (onSaveSuccess) {
          onSaveSuccess(newSelected);
        }
      }
    } catch (error) {
      console.error("Save decisions error:", error);
      message.error("Failed to save skill decisions.");
    } finally {
      setLoadingDecisions(false);
    }
  };

  const handleSave = async () => {
    if (!effectiveId) return;
    setLoading(true);
    try {
      await dispatch(saveCourseSkills(effectiveId, selectedSkills));
      message.success("Skills saved successfully!");
      if (onSaveSuccess) {
        onSaveSuccess(selectedSkills);
      }
    } catch (error) {
      console.error("Save error:", error);
      message.error("Failed to save skills.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingCourse || (!courseInfo?._id && !courseInfo?.course_id)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Spin size="large" tip="Loading course details..." />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-body">
        {/* Header Section */}
        <div className="mb-4">
          <div
            className="d-flex align-items-center justify-content-between mb-3"
          >
            <h4
              className="card-title mb-0"
              style={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#111827",
              }}
            >
              Course Skills
            </h4>
            {effectiveId && (
              <AIGenerateAction
                onGenerateSuccess={handleAIGenerateSkills}
                category="Skills"
                mode="json"
                showText={true}
                buttonText="Generate Skills"
                courseId={effectiveId}
                systemPromptDefault="You are an expert curriculum analyst building a clean course skill taxonomy. Extract concrete, searchable skills, tools, frameworks, methods, and technologies that a learner can intentionally acquire from the course."
                contextInstructions="Return ONLY one minified JSON object in this exact shape: {&quot;skills&quot;:[&quot;Skill Name&quot;]}. Include 5 to 12 concise skill names. Use canonical, searchable names suitable for a master skill taxonomy. Prefer specific skills, tools, frameworks, techniques, and methods over broad categories. Do not include job roles, course formats, marketing phrases, difficulty levels, or duplicate/near-duplicate variants. Keep each skill between 1 and 5 words. Do not include reasoning, analysis, markdown, notes, confidence scores, or explanatory text."
                defaultContextFields={["title", "headlines", "out_come", "curriculum"]}
                excludeContextFields={["skills"]}
              />
            )}
          </div>
        </div>

        {/* Matched Skills Section */}
        {matchedSkills.length > 0 && (
          <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0" style={{ color: '#389e0d', fontSize: '15px' }}>
                <CheckCircleOutlined className="mr-2" />
                Matched Skills ({matchedSkills.length})
              </h5>
              <Space>
                <Button
                  size="small"
                  onClick={handleDisambiguate}
                  loading={loadingDisambiguate}
                  style={{ borderColor: '#389e0d', color: '#389e0d' }}
                >
                  Run AI Disambiguation
                </Button>
                <Button 
                  size="small" 
                  icon={<SettingOutlined />} 
                  onClick={() => setDisambiguateConfigVisible(true)} 
                  style={{ color: '#389e0d', borderColor: '#389e0d' }}
                />
              </Space>
            </div>
            <div className="d-flex flex-column" style={{ gap: '12px' }}>
              {matchedSkills.map((ms, idx) => (
                <div key={idx} className="d-flex align-items-center justify-content-between p-2 bg-white border rounded">
                  <div className="d-flex flex-column">
                    <Space align="center" style={{ marginBottom: '4px' }}>
                      <Text strong>{ms.generated}</Text>
                      {ms.disambiguated && (
                        <Tag 
                          color={
                            ms.classification === 'SYNONYM' ? 'green' : 
                            ms.classification === 'HYPONYM' ? 'orange' : 
                            ms.classification === 'DISTINCT' ? 'red' : 'default'
                          } 
                          style={{ margin: 0, borderRadius: '4px' }}
                        >
                          {ms.classification}
                        </Tag>
                      )}
                    </Space>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Suggested Match: <Tag color="green" className="ml-1">{ms.matchedSkill?.name}</Tag>
                      <Badge 
                        count={`${(ms.confidence * 100).toFixed(0)}%`} 
                        style={{ backgroundColor: '#52c41a', marginLeft: '8px' }} 
                      />
                      {ms.layer && (
                        <Tag 
                          color={ms.layer === 'semantic' ? 'purple' : 'geekblue'} 
                          style={{ marginLeft: '8px', borderRadius: '12px', padding: '0 8px' }}
                        >
                          {ms.layer === 'semantic' ? 'Semantic (AI)' : 'Fuzzy (Text)'} Match
                        </Tag>
                      )}
                    </Text>
                    {ms.disambiguated && ms.reasoning && (
                      <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', fontStyle: 'italic', maxWidth: '600px' }}>
                        "{ms.reasoning}"
                      </Text>
                    )}
                  </div>
                  <Space>
                    <Button 
                      type={ms.decision === 'accept' ? 'primary' : 'default'} 
                      onClick={() => updateMatchedDecision(idx, 'accept')}
                      size="small"
                    >
                      Accept Match
                    </Button>
                    <Button 
                      onClick={() => updateMatchedDecision(idx, 'save_as_new')}
                      size="small"
                    >
                      Save as New
                    </Button>
                    <Button
                      onClick={() => updateMatchedDecision(idx, 'create_child')}
                      size="small"
                      disabled={!ms.matchedSkill?._id}
                    >
                      Create as Child
                    </Button>
                    <Button 
                      type={ms.decision === 'discard' ? 'primary' : 'text'} 
                      danger 
                      onClick={() => updateMatchedDecision(idx, 'discard')}
                      size="small"
                    >
                      Discard
                    </Button>
                  </Space>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Skills Section */}
        {newSkills.length > 0 && (
          <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0" style={{ color: '#d48806', fontSize: '15px' }}>
                <PlusOutlined className="mr-2" />
                New Skills ({newSkills.length})
              </h5>
              <Button 
                size="small" 
                onClick={handleApproveAllNewSkills}
                style={{ borderColor: '#d48806', color: '#d48806' }}
              >
                Approve All
              </Button>
            </div>
            <div className="d-flex flex-column" style={{ gap: '12px' }}>
              {newSkills.map((ns, idx) => (
                <div key={idx} className="d-flex align-items-center justify-content-between p-2 bg-white border rounded">
                  <Space style={{ flex: 1 }}>
                    <Input 
                      value={ns.name} 
                      onChange={e => updateNewSkill(idx, 'name', e.target.value)}
                      placeholder="Skill Name"
                      style={{ width: '200px' }}
                    />
                    <Select
                      placeholder="Select Parent Skill (Optional)"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={options}
                      value={ns.parentId}
                      onChange={val => updateNewSkill(idx, 'parentId', val)}
                      style={{ width: '250px' }}
                    />
                    {ns.suggestedParentName && (
                      <Tag color="geekblue">
                        Child of {ns.suggestedParentName}
                      </Tag>
                    )}
                  </Space>
                  <Space>
                    <Button 
                      type={ns.decision === 'save' ? 'primary' : 'default'} 
                      onClick={() => updateNewSkill(idx, 'decision', 'save')}
                      size="small"
                    >
                      Approve & Save
                    </Button>
                    <Button 
                      type={ns.decision === 'discard' ? 'primary' : 'text'} 
                      danger 
                      onClick={() => updateNewSkill(idx, 'decision', 'discard')}
                      size="small"
                    >
                      Discard
                    </Button>
                  </Space>
                </div>
              ))}
            </div>
          </div>
        )}

        {(matchedSkills.length > 0 || newSkills.length > 0) && (
          <Button 
             type="primary" 
             loading={loadingDecisions}
             onClick={handleApproveDecisions}
             className="w-100 mb-4"
             style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
          >
             Save Review Decisions
          </Button>
        )}

        {/* Skills Select */}
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%" }}
          placeholder="Select skills for this course"
          options={options}
          value={selectedSkills}
          onChange={(newValues) => setSelectedSkills(newValues)}
          optionFilterProp="label"
        />

        {/* Selected Skills Tags */}
        <div className="mt-3">
          {selectedSkillLabels.length > 0 ? (
            selectedSkillLabels.map((label) => (
              <Tag key={label} color="blue" className="mb-1">
                {label}
              </Tag>
            ))
          ) : (
            <Text type="secondary">
              No skills selected yet. You can assign manually or generate with
              AI.
            </Text>
          )}
        </div>

        {/* Footer Action */}
        <div
          className="d-flex justify-content-end mt-4 pt-4"
          style={{ borderTop: "1px solid #f0f0f0" }}
        >
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={loading}
            style={{
              minWidth: "140px",
              height: "36px",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "6px",
              boxShadow: "0 2px 4px rgba(24, 144, 255, 0.15)",
              background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
              border: "none",
            }}
          >
            Save Skills
          </Button>
        </div>
      </div>

      <AIGeneratorConfig
        visible={disambiguateConfigVisible}
        onClose={() => setDisambiguateConfigVisible(false)}
        category="Skills Disambiguation"
        defaultPrompt="You are an expert taxonomy assistant. Classify the relationship between the generated skill and the matched taxonomy skill."
        contextInstructions="Return a JSON object with 'classification', 'suggestedParent', and 'reasoning' keys."
        excludeContextFields={["curriculum", "out_come", "skills"]}
      />
    </div>
  );
};

export default CourseSkillsTab;
